import { CAREERS_URL } from "./landing_pages/navigation/config/navConfig";
import { Link } from "react-router-dom";
import { rebrandEnabled } from "./util/brandingFlag";

type FooterLink = {
  title: string;
  href: string;
  isExternal?: boolean;
};

const COMPANY_LINKS: FooterLink[] = [
  { title: "Contact", href: "/contact_us" },
  { title: "HIPAA NPP", href: "/hipaa_notice.pdf", isExternal: true },
  { title: "Careers", href: CAREERS_URL, isExternal: true },
  { title: "About Us", href: "/about_us" },
];

const SOCIAL_LINKS: FooterLink[] = [
  {
    title: "LinkedIn",
    href: "https://www.linkedin.com/company/versemed/",
    isExternal: true,
  },
];

const LEGAL_LINKS: FooterLink[] = [
  { title: "Patient Rights", href: "/patient-rights" },
  { title: "Terms of Service", href: "/terms" },
];

const linkStyle =
  "tw-font-sohne tw-text-[16px] tw-leading-[1.4] tw-text-white tw-no-underline hover:tw-text-gray-5 hover:tw-underline";

const FooterLinkItem = ({ link }: { link: FooterLink }) => {
  if (link.isExternal) {
    return (
      <a
        href={link.href}
        target="_blank"
        rel="noopener noreferrer"
        className={linkStyle}
      >
        {link.title}
      </a>
    );
  }

  return (
    <Link to={link.href} className={linkStyle}>
      {link.title}
    </Link>
  );
};

const FooterLinkColumn = ({
  title,
  links,
}: {
  title: string;
  links: FooterLink[];
}) => (
  <div className="tw-flex tw-flex-col tw-gap-[16px]">
    <span className="tw-font-sohne-mono tw-text-[14px] tw-uppercase tw-leading-[1.4] tw-tracking-[0.28px] tw-text-gray-5">
      {title}
    </span>
    <div className="tw-flex tw-flex-col tw-gap-[16px]">
      {links.map((link) => (
        <FooterLinkItem key={link.title} link={link} />
      ))}
    </div>
  </div>
);

function RebrandedFooter() {
  return (
    <div
      id="footer"
      className="tw-bg-verse-tide tw-px-[72px] tw-py-[56px] 720:tw-px-[16px]"
    >
      {/* 248px keeps the columns spread to the design's fixed 360px footer. */}
      <div className="tw-flex tw-min-h-[248px] tw-gap-[24px] 720:tw-min-h-0 720:tw-flex-col 720:tw-gap-[72px]">
        <div className="tw-flex tw-flex-1 tw-flex-col tw-justify-between tw-gap-[32px]">
          <img
            src="/rebranded/verse-lockup-white.png"
            alt="Verse Medical"
            className="tw-h-[41px] tw-w-[120px]"
          />
          <div className="tw-flex tw-gap-[56px] 720:tw-flex-col 720:tw-gap-[16px]">
            {LEGAL_LINKS.map((link) => (
              <FooterLinkItem key={link.title} link={link} />
            ))}
          </div>
        </div>
        <div className="tw-flex tw-shrink-0 tw-flex-col tw-justify-between tw-gap-[32px]">
          <div className="tw-flex tw-gap-[64px]">
            <FooterLinkColumn title="Company" links={COMPANY_LINKS} />
            <FooterLinkColumn title="Social" links={SOCIAL_LINKS} />
          </div>
          <span className="tw-font-sohne tw-text-[18px] tw-leading-[1.4] tw-text-gray-5">
            Copyright {new Date().getFullYear()} Verse Medical
          </span>
        </div>
      </div>
    </div>
  );
}

function LegacyFooter() {
  const columnHeadingStyle =
    "tw-text-blue-dark-1 tw-font-bold tw-tracking-[1.5px]";
  const linkStyle = "tw-block tw-mt-[8px]";

  return (
    <div
      id="footer"
      className="tw-border-0 tw-border-t-[1px] tw-border-solid tw-border-blue-light-2 tw-bg-blue-light-1 tw-p-[20px] tw-text-[14px]"
    >
      <div className="tw-mx-auto tw-px-[15px]">
        <img
          src="/alternate_logo.png"
          className="tw-mb-[20px] tw-h-[50px] tw-px-[15px]"
        />
        <div className="tw-grid tw-grid-cols-4 740:tw-grid-cols-2">
          <div className="tw-mb-[20px] tw-px-[15px]">
            <div className={columnHeadingStyle}>COMPANY</div>
            <div className="tw-mt-[10px] tw-text-custom-blue">
              <Link to="/terms" className={linkStyle}>
                Terms of Service
              </Link>
              <Link to="/patient-rights" className={linkStyle}>
                Patient Rights
              </Link>
              <a href="/hipaa_notice.pdf" className={linkStyle}>
                HIPAA NPP
              </a>
              <a href={CAREERS_URL} className={linkStyle}>
                Careers
              </a>
            </div>
          </div>
          <div className="tw-mb-[20px] tw-px-[15px]">
            <div className={columnHeadingStyle}>SUPPORT</div>
            <div className="tw-mt-[10px] tw-text-custom-blue">
              <Link to="/terms#returns" className={linkStyle}>
                Return Policy
              </Link>
              <Link to="/contact_us" className={linkStyle}>
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Footer() {
  return rebrandEnabled() ? <RebrandedFooter /> : <LegacyFooter />;
}
